import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useScrollReveal } from "../hooks/useScrollReveal";

const VaccinationPolicyPage = () => {
  const headerReveal = useScrollReveal({ threshold: 0.1 });
  const contentReveal = useScrollReveal({ threshold: 0.05 });

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-24">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${
            headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            💉 Vaccination Policy
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Last Updated: 22-04-2026
          </p>
        </div>
      </section>

      <section className="bg-gradient-to-b from-white to-gray-50 py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div
            ref={contentReveal.ref}
            className={`max-w-[1020px] mx-auto bg-white/95 rounded-3xl p-6 sm:p-10 lg:p-12 shadow-soft ring-1 ring-gray-100 text-left transition-all duration-1000 ${
              contentReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
          >
            <div className="py-7 border-b border-gray-100">
              <p className="text-lg text-gray-600 leading-relaxed">
                At Domigo, the health and safety of all pets in our care is our highest priority. To maintain a safe and hygienic environment, we require all pets to meet the following vaccination standards before availing any of our services.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                1. Mandatory Vaccinations
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                All dogs must be up-to-date with the following core vaccinations:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Rabies</li>
                <li>DHPPi / 7-in-1 / 9-in-1 (Distemper, Hepatitis, Parvovirus, Parainfluenza, etc.)</li>
              </ul>
              <p className="text-lg text-gray-600 leading-relaxed">
                Additional vaccinations may be recommended based on the pet's lifestyle and exposure.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                2. Vaccination Proof
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Pet parents must provide valid and verifiable vaccination records during onboarding.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Records should include:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Vaccine name</li>
                <li>Date of administration</li>
                <li>Validity/expiry date</li>
                <li>Veterinarian details</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                3. Expired or Missing Vaccinations
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Pets with expired, incomplete, or missing vaccinations will not be admitted.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Entry will only be allowed once updated vaccination proof is submitted and verified.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                4. Vaccination Timing
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Vaccinations must be completed at least 7 days prior to check-in.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This ensures adequate time for immunity development and reduces risk of infection.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                5. Health Declaration
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Any recent illness, symptoms, or exposure to contagious diseases must be disclosed during onboarding.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Domigo reserves the right to deny admission if a pet shows signs of illness.
              </p>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                6. Preventive Care (Recommended)
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                While not mandatory, the following are strongly recommended:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Regular deworming</li>
                <li>Flea and tick prevention</li>
                <li>Routine health check-ups</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                7. Right to Refuse Admission
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                Domigo reserves the right to refuse or cancel a booking if:
              </p>
              <ul className="list-disc pl-6 text-lg text-gray-600 leading-relaxed space-y-2 mb-4">
                <li>Vaccination requirements are not met</li>
                <li>Provided records are inaccurate or incomplete</li>
                <li>The pet is deemed a health risk to others</li>
              </ul>
            </div>

            <div className="space-y-4 py-7 border-b border-gray-100">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                8. Policy Updates
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                This Vaccination Policy may be updated periodically to align with veterinary guidelines and safety standards.
              </p>
            </div>

            <div className="space-y-4 pt-7">
              <h2 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent mb-4">
                9. Contact Us
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                For any questions regarding vaccination requirements, please contact:
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Domigo
                <br />
                Indore Bypass Road, near Kushabhau Thackrey Road,
                <br />
                Opposite Bharat Benz Showroom,
                <br />
                Indore, Madhya Pradesh – 452016
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VaccinationPolicyPage;